/**
 * UI state.
 *
 * Only what the user chose lives here. Server data belongs to React Query,
 * which already handles caching and invalidation -- duplicating it in a
 * store is how two sources of truth start disagreeing.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { AssetTab } from '@/components/filters/assetFilter'
import type { TimeframeKey } from '@/theme/chart'

export type BottomTab = 'news' | 'events' | 'backtest' | 'market' | 'sources'

interface AppState {
  selectedTicker: string | null
  timeframe: TimeframeKey
  bottomTab: BottomTab

  // The symbol filter, shared by the watchlist and the market-signal tab so
  // that narrowing to "CK VN" in one narrows the other. Two panels showing
  // different universes at the same time is how a reader ends up believing
  // a signal was missed.
  assetTab: AssetTab
  symbolQuery: string
  cryptoSort: CryptoSort

  // Chart overlay toggles. The spec asks for these explicitly: with every
  // overlay on at once the chart becomes unreadable.
  showEma: boolean
  showBollinger: boolean
  showZones: boolean
  showFib: boolean
  showSignalLines: boolean
  showVolume: boolean

  wsConnected: boolean

  selectTicker: (ticker: string) => void
  setTimeframe: (timeframe: TimeframeKey) => void
  setBottomTab: (tab: BottomTab) => void
  setAssetTab: (tab: AssetTab) => void
  setSymbolQuery: (query: string) => void
  setCryptoSort: (sort: CryptoSort) => void
  toggle: (key: OverlayToggle) => void
  setWsConnected: (connected: boolean) => void
}

export type CryptoSort = 'turnover' | 'change'

export type OverlayToggle =
  | 'showEma' | 'showBollinger' | 'showZones'
  | 'showFib' | 'showSignalLines' | 'showVolume'

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      selectedTicker: null,
      timeframe: '4h',
      bottomTab: 'news',

      assetTab: 'all',
      symbolQuery: '',
      cryptoSort: 'turnover',

      showEma: true,
      showBollinger: false,
      showZones: true,
      showFib: false,
      showSignalLines: true,
      showVolume: true,

      wsConnected: false,

      selectTicker: (ticker) => set({ selectedTicker: ticker }),
      setTimeframe: (timeframe) => set({ timeframe }),
      setBottomTab: (bottomTab) => set({ bottomTab }),
      setAssetTab: (assetTab) => set({ assetTab }),
      setSymbolQuery: (symbolQuery) => set({ symbolQuery }),
      setCryptoSort: (cryptoSort) => set({ cryptoSort }),
      toggle: (key) => set((state) => ({ [key]: !state[key] }) as Partial<AppState>),
      setWsConnected: (wsConnected) => set({ wsConnected }),
    }),
    {
      name: 'isp-ui',
      // Deliberately not persisting wsConnected: a stale "connected" badge
      // after a reload would be a lie.
      partialize: (state) => ({
        selectedTicker: state.selectedTicker,
        timeframe: state.timeframe,
        bottomTab: state.bottomTab,
        assetTab: state.assetTab,
        cryptoSort: state.cryptoSort,
        // symbolQuery is deliberately not persisted: coming back to a
        // dashboard silently filtered by something typed yesterday reads as
        // missing data, not as a saved preference.
        showEma: state.showEma,
        showBollinger: state.showBollinger,
        showZones: state.showZones,
        showFib: state.showFib,
        showSignalLines: state.showSignalLines,
        showVolume: state.showVolume,
      }),
    },
  ),
)
